import React from 'react'
import { FieldValues, useController, UseControllerProps } from 'react-hook-form'
import { PhoneInput } from 'react-international-phone'
import { useIntl } from 'react-intl'

import 'react-international-phone/style.css'
import s from './styles.module.scss'

import {
  isPhoneValid,
  PHONE_FIELD_SUPPORTED_COUNTRIES,
} from './helpers/phone-field.helper'
import { ITextInputProps } from '../input-type-text/input-type-text'
import { Iso3166Alpha2Enum } from '../../../lib/graphql'

/**
 * DOCUMENTATION : https://react-international-phone.vercel.app/
 */
type ControlledPhoneFieldProps<T extends FieldValues> = Omit<
  UseControllerProps<T>,
  'shouldUnregister'
> &
  Pick<ITextInputProps, 'title' | 'disable' | 'dataTestId'>

export const ControlledPhoneField = <T extends FieldValues>({
  ...props
}: ControlledPhoneFieldProps<T>) => {
  const intl = useIntl()
  const { title, disable, rules, dataTestId, ...fieldProps } = props

  const { field, fieldState } = useController({
    ...fieldProps,
    rules: { ...rules, ...(rules?.required && { validate: isPhoneValid }) },
  })

  const hasError =
    (field.value && !isPhoneValid(field.value)) || fieldState.error?.message

  const errorMessage = fieldState.error?.message

  const inputProps = {
    ...field,
    ...fieldProps,
  }

  return (
    <div className={s.container} data-testid={dataTestId ?? inputProps.name}>
      <span className={s.title}>{title}</span>

      <PhoneInput
        {...inputProps}
        value={field.value ?? ''}
        inputStyle={{
          width: '100%',
          height: 40,
          ...(errorMessage && { borderColor: 'red' }),
        }}
        countrySelectorStyleProps={{
          buttonStyle: {
            height: '100%',
            ...(errorMessage && { borderColor: 'red' }),
            padding: '0 10px',
          },
          dropdownStyleProps: { style: { width: 'auto' } },
        }}
        disabled={disable}
        disableDialCodePrefill={!rules?.required}
        preferredCountries={[Iso3166Alpha2Enum.FR.toLowerCase()]}
        defaultCountry={Iso3166Alpha2Enum.FR.toLowerCase()}
        countries={PHONE_FIELD_SUPPORTED_COUNTRIES}
      />

      {hasError ? (
        <span className={s.errorMessage}>
          {errorMessage ||
            intl.formatMessage({
              id: 'ui.ControlledPhoneField.invalidPhone',
              defaultMessage: 'Numéro invalide.',
            })}
        </span>
      ) : null}
    </div>
  )
}
